import React, { useState, useRef } from 'react';
import { ApolloError, useMutation } from '@apollo/client';
import { format } from 'date-fns';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { Blocker } from 'src/components/Modals/Block';
import { v4 as uuid } from 'uuid';

import { useSnackbar } from '../../providers/snackbar/hook/useSnackbar';
import { getToken } from '../../utils/localStorage';
import {
  streamConversation,
  streamContinueConversation,
} from '../../utils/conversationStream';
import type { ConversationProgressEvent } from '../../utils/conversationStreamEvents';
import { progressEventToSteps } from '../../utils/conversationStreamEvents';
import {
  defaultStreamLanguageBeforeSse,
  type StreamLang,
} from '../../utils/detectStreamLanguage';
import { mapStreamResponseLanguage } from '../../utils/streamResponseLanguage';
import {
  CONTINUE_CONVERSATION,
  START_CONVERSATION,
  UPDATE_CONVERSATION_CONTEXT,
} from '../../utils/gql/mutations';
import {
  ConversationMessage,
  ConversationMessageRole,
  Mutation,
  MutationContinueConversationArgs,
  MutationStartConversationArgs,
  UpdateConversationContextVariables,
} from '../../utils/gql/types';

import { InputBlock } from './components/InputBlock';
import { Messages } from './components/Messages';
import { Preview } from './components/Preview';
import { Wrapper } from './styled';
import { GptForm, UpdateConversationContextPayload } from './types';

const createUserMessage = (input: string): ConversationMessage => {
  return {
    id: uuid(),
    createdAt: new Date().toString(),
    role: ConversationMessageRole.User,
    text: input,
  };
};

const getCurrentDate = (): string => {
  return format(new Date(), "yyyy-MM-dd'T'HH:mm:ss.SSSXXX");
};

const getFinalizingLabel = (lang: StreamLang) => {
  switch (lang) {
    case 'en':
      return 'Analyzing and validating the answer…';
    case 'es':
      return 'Analizando y validando la respuesta…';
    case 'fr':
      return 'Analyse et validation de la réponse…';
    case 'de':
      return 'Antwort wird analysiert und validiert…';
    case 'pt':
      return 'Analisando e validando a resposta…';
    case 'it':
    default:
      return 'Analizzo e convalido la risposta…';
  }
};

export const GPT = () => {
  const snackbar = useSnackbar();

  const [currentConversationId, setCurrentConversationId] = useState<string>();
  const [isStreaming, setIsStreaming] = useState(false);
  const [isFinalizingStream, setIsFinalizingStream] = useState(false);
  const [streamLang, setStreamLang] = useState<StreamLang>(
    defaultStreamLanguageBeforeSse,
  );
  const [streamProgressEvents, setStreamProgressEvents] = useState<
    ConversationProgressEvent[]
  >([]);
  const resetStreamProgress = () => {
    setStreamProgressEvents([]);
  };

  const handleStreamProgress = (ev: ConversationProgressEvent) => {
    if (ev.type === 'response_language') {
      setStreamLang(mapStreamResponseLanguage(ev.language));
      return;
    }
    setStreamProgressEvents((prev) => [...prev, ev]);
  };

  const progressUiLang = streamLang;

  const methods = useForm<GptForm>({
    mode: 'all',
    defaultValues: {
      input: '',
      messages: [],
    },
  });

  const handleError = (message: string) => {
    snackbar(message);
  };

  const handleGraphqlError = (err: ApolloError) => {
    const { graphQLErrors, message: errorText } = err;
    const message = graphQLErrors.length ? graphQLErrors[0].message : errorText;

    handleError(message);
  };

  const [startConversationMutation, { loading: startConversationLoading }] =
    useMutation<
      Pick<Mutation, 'startConversation'>,
      MutationStartConversationArgs
    >(START_CONVERSATION, {
      onError: handleGraphqlError,
    });

  const [
    continueConversationMutation,
    { loading: continueConversationLoading },
  ] = useMutation<
    Pick<Mutation, 'continueConversation'>,
    MutationContinueConversationArgs
  >(CONTINUE_CONVERSATION, {
    onError: handleGraphqlError,
  });

  const [updateConversationContextMutation] = useMutation<
    Pick<Mutation, 'updateConversationContext'>,
    UpdateConversationContextVariables
  >(UPDATE_CONVERSATION_CONTEXT, {
    onError: handleGraphqlError,
  });

  const streamingRef = useRef(false);
  const lastChunkAtRef = useRef<number>(0);
  const hasChunkRef = useRef(false);
  const finalizeTimerRef = useRef<number | null>(null);

  const startConversation = async (userInput: string, displayText?: string) => {
    const displayMessage = displayText || userInput;
    const userInputMessage = createUserMessage(displayMessage);

    methods.setValue('messages', [userInputMessage]);

    const token = getToken();
    if (token) {
      streamingRef.current = true;
      setIsStreaming(true);
      setIsFinalizingStream(false);
      hasChunkRef.current = false;
      lastChunkAtRef.current = 0;
      resetStreamProgress();

      // Mostra lo stato "finalizzazione" solo quando i chunk si fermano per un attimo.
      if (finalizeTimerRef.current) {
        window.clearInterval(finalizeTimerRef.current);
      }
      finalizeTimerRef.current = window.setInterval(() => {
        if (!streamingRef.current) return;
        if (!hasChunkRef.current) return;
        const msSinceLastChunk = Date.now() - (lastChunkAtRef.current || 0);
        setIsFinalizingStream(msSinceLastChunk > 650);
      }, 200);

      try {
        await streamConversation(userInput, getCurrentDate(), token, {
          onProgress: handleStreamProgress,
          onChunk: (delta) => {
            if (!streamingRef.current) return;
            setIsStreaming(false);
            setIsFinalizingStream(false);
            hasChunkRef.current = true;
            lastChunkAtRef.current = Date.now();
            const prev = methods.getValues('messages');
            const last = prev[prev.length - 1];
            if (last?.role === ConversationMessageRole.Assistant) {
              methods.setValue('messages', [
                ...prev.slice(0, -1),
                { ...last, text: last.text + delta },
              ]);
            } else {
              methods.setValue('messages', [
                ...prev,
                {
                  id: 'streaming',
                  createdAt: new Date().toISOString(),
                  role: ConversationMessageRole.Assistant,
                  text: delta,
                },
              ]);
            }
          },
          onDone: (payload) => {
            streamingRef.current = false;
            setIsStreaming(false);
            setIsFinalizingStream(false);
            resetStreamProgress();
            if (finalizeTimerRef.current) {
              window.clearInterval(finalizeTimerRef.current);
              finalizeTimerRef.current = null;
            }
            const prev = methods.getValues('messages');
            const finalText = payload.message?.text ?? '';
            const finalId = payload.message?.id ?? 'assistant';
            const finalCreatedAt =
              payload.message?.createdAt ?? new Date().toISOString();
            const finalTable = payload.message?.table as any;
            const finalAttachments = payload.message?.attachments ?? undefined;

            const updated = prev.map((m) => {
              if (m.id !== 'streaming') return m;
              return {
                ...m,
                id: finalId,
                createdAt: finalCreatedAt,
                role: ConversationMessageRole.Assistant,
                text: finalText,
                table: finalTable,
                attachments: finalAttachments,
              } as ConversationMessage;
            });

            // Se per qualche motivo il messaggio streaming non esiste (es. nessun chunk),
            // appendiamo comunque la risposta finale.
            const hasStreamingMsg = prev.some((m) => m.id === 'streaming');
            methods.setValue(
              'messages',
              hasStreamingMsg
                ? updated
                : [
                    ...prev,
                    {
                      id: finalId,
                      createdAt: finalCreatedAt,
                      role: ConversationMessageRole.Assistant,
                      text: finalText,
                      table: finalTable,
                      attachments: finalAttachments,
                    } as ConversationMessage,
                  ],
            );
            setCurrentConversationId(payload.conversationId);
          },
          onError: (message) => {
            streamingRef.current = false;
            setIsStreaming(false);
            setIsFinalizingStream(false);
            resetStreamProgress();
            if (finalizeTimerRef.current) {
              window.clearInterval(finalizeTimerRef.current);
              finalizeTimerRef.current = null;
            }
            handleError(message);
            startConversationFallback(userInput, userInputMessage);
          },
        });
      } catch (e) {
        streamingRef.current = false;
        setIsStreaming(false);
        setIsFinalizingStream(false);
        resetStreamProgress();
        if (finalizeTimerRef.current) {
          window.clearInterval(finalizeTimerRef.current);
          finalizeTimerRef.current = null;
        }
        handleError(e instanceof Error ? e.message : 'Errore di connessione');
        startConversationFallback(userInput, userInputMessage);
      }
      return;
    }

    await startConversationFallback(userInput, userInputMessage);
  };

  const startConversationFallback = async (
    userInput: string,
    userInputMessage: ConversationMessage,
  ) => {
    const { data: startConversationResponse } = await startConversationMutation(
      {
        variables: {
          userInput,
          currentDate: getCurrentDate(),
        },
      },
    );

    if (startConversationResponse?.startConversation) {
      methods.setValue(
        'messages',
        startConversationResponse.startConversation.messages,
      );
      setCurrentConversationId(startConversationResponse.startConversation.id);
    } else {
      methods.setValue('messages', [userInputMessage]);
    }
  };

  const continueConversation = async (
    userInput: string,
    displayText?: string,
  ) => {
    if (!currentConversationId) {
      handleError('Conversation id not found');
      return;
    }

    const prevMessages = methods.getValues('messages');
    const displayMessage = displayText || userInput;
    const userInputMessage = createUserMessage(displayMessage);

    methods.setValue('messages', [...prevMessages, userInputMessage]);

    const token = getToken();
    if (token) {
      streamingRef.current = true;
      setIsStreaming(true);
      setIsFinalizingStream(false);
      hasChunkRef.current = false;
      lastChunkAtRef.current = 0;
      resetStreamProgress();
      if (finalizeTimerRef.current)
        window.clearInterval(finalizeTimerRef.current);
      finalizeTimerRef.current = window.setInterval(() => {
        if (!streamingRef.current) return;
        if (!hasChunkRef.current) return;
        const msSinceLastChunk = Date.now() - (lastChunkAtRef.current || 0);
        setIsFinalizingStream(msSinceLastChunk > 650);
      }, 200);

      try {
        await streamContinueConversation(
          currentConversationId,
          userInput,
          getCurrentDate(),
          token,
          {
            onProgress: handleStreamProgress,
            onChunk: (delta) => {
              if (!streamingRef.current) return;
              setIsStreaming(false);
              setIsFinalizingStream(false);
              hasChunkRef.current = true;
              lastChunkAtRef.current = Date.now();
              const prev = methods.getValues('messages');
              const last = prev[prev.length - 1];
              if (last?.role === ConversationMessageRole.Assistant) {
                methods.setValue('messages', [
                  ...prev.slice(0, -1),
                  { ...last, text: last.text + delta },
                ]);
              } else {
                methods.setValue('messages', [
                  ...prev,
                  {
                    id: 'streaming',
                    createdAt: new Date().toISOString(),
                    role: ConversationMessageRole.Assistant,
                    text: delta,
                  },
                ]);
              }
            },
            onDone: (payload) => {
              streamingRef.current = false;
              setIsStreaming(false);
              setIsFinalizingStream(false);
              resetStreamProgress();
              if (finalizeTimerRef.current) {
                window.clearInterval(finalizeTimerRef.current);
                finalizeTimerRef.current = null;
              }
              const prev = methods.getValues('messages');
              const finalText = payload.message?.text ?? '';
              const finalId = payload.message?.id ?? 'assistant';
              const finalCreatedAt =
                payload.message?.createdAt ?? new Date().toISOString();
              const finalTable = payload.message?.table as any;
              const finalAttachments =
                payload.message?.attachments ?? undefined;
              const updated = prev.map((m) => {
                if (m.id !== 'streaming') return m;
                return {
                  ...m,
                  id: finalId,
                  createdAt: finalCreatedAt,
                  role: ConversationMessageRole.Assistant,
                  text: finalText,
                  table: finalTable,
                  attachments: finalAttachments,
                } as ConversationMessage;
              });
              const hasStreamingMsg = prev.some((m) => m.id === 'streaming');
              methods.setValue(
                'messages',
                hasStreamingMsg
                  ? updated
                  : [
                      ...prev,
                      {
                        id: finalId,
                        createdAt: finalCreatedAt,
                        role: ConversationMessageRole.Assistant,
                        text: finalText,
                        table: finalTable,
                        attachments: finalAttachments,
                      } as ConversationMessage,
                    ],
              );
            },
            onError: (msg) => {
              streamingRef.current = false;
              setIsStreaming(false);
              setIsFinalizingStream(false);
              resetStreamProgress();
              if (finalizeTimerRef.current) {
                window.clearInterval(finalizeTimerRef.current);
                finalizeTimerRef.current = null;
              }
              handleError(msg);
              continueConversationFallback(userInput, userInputMessage);
            },
          },
        );
      } catch (e) {
        streamingRef.current = false;
        setIsStreaming(false);
        setIsFinalizingStream(false);
        resetStreamProgress();
        handleError(e instanceof Error ? e.message : 'Errore di connessione');
        continueConversationFallback(userInput, userInputMessage);
      }
      return;
    }

    await continueConversationFallback(userInput, userInputMessage);
  };

  const continueConversationFallback = async (
    userInput: string,
    userInputMessage: ConversationMessage,
  ) => {
    const prevMessages = methods.getValues('messages');
    const { data: continueConversationResponse } =
      await continueConversationMutation({
        variables: {
          conversationId: currentConversationId!,
          userInput,
          currentDate: getCurrentDate(),
        },
      });

    if (continueConversationResponse?.continueConversation) {
      const answerMessage = continueConversationResponse.continueConversation;
      methods.setValue('messages', [
        ...prevMessages,
        userInputMessage,
        answerMessage,
      ]);
    }
  };

  const updateConversationContext = async (
    payload: UpdateConversationContextPayload,
  ) => {
    if (!currentConversationId) {
      handleError('Conversation id not found');
      return;
    }

    await updateConversationContextMutation({
      variables: {
        args: {
          conversationId: currentConversationId,
          ...payload,
        },
      },
    });
  };

  const onSubmit: SubmitHandler<GptForm> = async ({ input, messages }) => {
    methods.resetField('input');

    if (messages.length === 0) {
      await startConversation(input);
    } else {
      await continueConversation(input);
    }
  };

  const handlePreviewButtonClick = (input: string, queries?: string[]) => {
    const messages = methods.getValues('messages');

    // Se ci sono query multiple, invia un marker speciale che il backend riconoscerà
    if (queries && queries.length > 0) {
      // Usa il testo originale per il display, il testo tecnico solo per il backend
      const technicalInput = `__MULTI_QUERY__:${input}__QUERIES__:${JSON.stringify(queries)}`;

      if (messages.length === 0) {
        startConversation(technicalInput, input);
      } else {
        continueConversation(technicalInput, input);
      }
    } else {
      onSubmit({
        input,
        messages,
      });
    }
  };

  const loading =
    startConversationLoading || continueConversationLoading || isStreaming;

  return (
    <Wrapper>
      <FormProvider {...methods}>
        {methods.watch('messages').length ? (
          <Messages
            loading={loading}
            updateConversationContext={updateConversationContext}
            isFinalizingStream={isFinalizingStream}
            finalizingLabel={getFinalizingLabel(streamLang)}
            streamProgressSteps={progressEventToSteps(
              streamProgressEvents,
              progressUiLang,
            )}
          />
        ) : (
          <Preview onChangePreviewButtonClick={handlePreviewButtonClick} />
        )}

        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <InputBlock onSubmit={methods.handleSubmit(onSubmit)} />
        </form>
      </FormProvider>
      <Blocker isBlocked={!!methods.watch('messages').length} />
    </Wrapper>
  );
};
